import { create } from 'zustand';
import {
  loadProgression, saveProgression, effectiveStats,
  PLANES, PERKS, planeById, type Progression, type EffectiveStats,
} from './progression';

export type Phase = 'splash' | 'hangar' | 'playing' | 'gameover' | 'victory';

/** The four authored beats in the opening training sortie. Internal substeps (steer, climb,
 *  boost, crew the gun, open the map) also live in tutorialDone, but only these mission beats
 *  count toward completion and the four-pip progress display. */
export interface TutorialTask { key: string; label: string; }
export const TUTORIAL_TASKS: TutorialTask[] = [
  { key: 'flight', label: 'FLIGHT CHECK' },
  { key: 'scout', label: 'INTERCEPT THE SCOUT' },
  { key: 'bomber', label: 'MAN THE AA GUN' },
  { key: 'order', label: 'COMMAND THE FRONT' },
];

/** Combo timing: a kill or near-miss extends the window; it decays after this. */
const COMBO_WINDOW = 4.0; // seconds

/** Persisted player settings (FX quality + accessibility) — survive reloads like progression. */
const SETTINGS_KEY = 'ww1-settings';
function loadSettings(): { fxLite: boolean; colorblind: boolean; reducedMotion: boolean } {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<{ fxLite: boolean; colorblind: boolean; reducedMotion: boolean }>;
      return { fxLite: !!p.fxLite, colorblind: !!p.colorblind, reducedMotion: !!p.reducedMotion };
    }
  } catch { /* ignore */ }
  // Respect the OS-level reduced-motion preference as the default.
  const osReduced = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  return { fxLite: false, colorblind: false, reducedMotion: osReduced };
}
/** Credits awarded = score * this rate, on run end. */
const CREDIT_RATE = 0.1;

export interface EnemyArrow {
  /** true if any enemy exists */
  any: boolean;
  /** true if nearest enemy is off-screen */
  offscreen: boolean;
  /** screen-space angle (rad) from center pointing toward enemy; used when offscreen */
  angle: number;
  /** normalized distance 0..1 (closer = smaller); used for scale */
  normDist: number;
}

export type RadarBlipKind = 'enemy' | 'zeppelin' | 'tank';
export interface RadarBlip {
  kind: RadarBlipKind;
  /** right-of-player, in world units (already player-relative, already yaw-rotated) */
  rx: number;
  /** forward-of-player, in world units (+ = ahead) */
  rz: number;
  /** Already shot down (wreckage) — drawn grey, ignored by targeting. */
  dying: boolean;
}

export type FriendlyKind = 'plane' | 'gun' | 'tank';
export interface RadarFriendly {
  kind: FriendlyKind;
  rx: number; rz: number;
  /** True for the unit the player is currently controlling — drawn highlighted. */
  controlled: boolean;
  /** 0..1 health remaining. */
  hpFrac: number;
  /** Recently took damage — drawn pulsing so the player can see who needs help. */
  threatened: boolean;
}
export interface RadarVillage {
  rx: number; rz: number;
  /** 0 = intact, 1 = every building gone. Drives the colour. */
  damageFrac: number;
  destroyed: boolean;
  /** Recently lost a building — drawn pulsing. */
  threatened: boolean;
}

/** A live threat the player should respond to — drives the directional banner. */
export interface ThreatAlert {
  label: string;
  /** Screen-space angle (rad) from camera-forward toward the threat. 0 = right, π/2 = up. */
  angleRad: number;
  /** True if the threat is roughly in front of the camera (don't draw an arrow on top of it). */
  inFront: boolean;
  severity: 'low' | 'high';
}

// --- Fullscreen tactical map (fixed world frame, north-up) ---
/** A friendly's current order, with the target already resolved to a world point (if any),
 *  so the map can draw a line from the unit to what it's doing. */
export interface MapOrder { type: 'patrol' | 'defend' | 'escort' | 'attack'; tx?: number; tz?: number; }
export interface ControlUnitStatus {
  id: number;
  label: string;
  hpFrac: number;
  kind: FriendlyKind;
  order: MapOrder['type'];
  /** Human-readable standing order for the compact command strip. */
  orderLabel: string;
}
export interface MapFriendly { id: number; kind: FriendlyKind; x: number; z: number; controlled: boolean; hpFrac: number; threatened: boolean; label: string; order: MapOrder; engage?: { x: number; z: number }; }
export interface MapEnemy { id: number; kind: RadarBlipKind; x: number; z: number; dying: boolean; }
export interface MapVillage { index: number; x: number; z: number; damageFrac: number; destroyed: boolean; threatened: boolean; }
export interface MapData {
  friendlies: MapFriendly[];
  enemies: MapEnemy[];
  villages: MapVillage[];
  /** World units mapped to the map's half-extent — covers the playable ring. */
  worldRadius: number;
  /** Mountain-ring radius (world units) so the map can draw the boundary. */
  ringRadius: number;
}

export interface EnemyMark {
  id: number;
  kind: RadarBlipKind;
  /** Normalized device coords, -1..1 */
  ndcX: number;
  ndcY: number;
  /** Distance in world units */
  dist: number;
  /** true if inside camera frustum */
  visible: boolean;
  /** true if this target is currently inside the auto-fire cone */
  inCone: boolean;
  /** Already shot down (wreckage) — reticle drawn grey. */
  dying: boolean;
}

/** Crewed-tank targeting computer: lock box on the target + a lead marker (where to put the
 *  barrel so the moving target is hit). NDC coords (-1..1); HUD maps to screen. null when the
 *  controlled unit isn't a manually-aimed tank or there's no target. */
export interface TankTargeting {
  lockNdcX: number; lockNdcY: number; lockVisible: boolean;
  leadNdcX: number; leadNdcY: number; leadVisible: boolean;
  /** Range to target, world units. */
  range: number;
  /** Barrel aligned with the lead point → firing solution good (draw green). */
  onSolution: boolean;
}

export interface GameState {
  phase: Phase;
  score: number;
  hiScore: number;
  wave: number;
  waveIntroAt: number;
  hp: number;
  maxHp: number;
  isFiring: boolean;
  muted: boolean;
  /** Persisted player settings (see SETTINGS_KEY): post-FX quality, accessibility. */
  fxLite: boolean;
  colorblind: boolean;
  reducedMotion: boolean;
  ai: boolean;
  boosting: boolean;
  /** Afterburner thermal state, driven by the controlled plane. `boostHeat` 0..1 fills while
   *  boosting and cools when off; at 1 the burner trips (`boostOverheat`) and locks out until it
   *  cools back down. The HUD boost button renders this as an amber→red ring + cutout flash. */
  boostHeat: number;
  boostOverheat: boolean;
  /** Engine tachometer + stall state, driven by the controlled plane's energy model. `rpmFrac` is
   *  airspeed / cruise (0 stalled · 1 cruise · >1.2 overspeed) → the RPM needle. `stallWarn` 0..1
   *  ramps as airspeed falls into the stall band (1 = mushing); the gauge redlines and the horn
   *  beeps faster on it. */
  rpmFrac: number;
  stallWarn: number;
  /** Full-screen white flash 0..1, driven by the UFO encounter (peaks as the saucer's whir
   *  crescendos, holds white over the teleport, fades back on the battlefield). */
  whiteout: number;
  /** Full-screen colour wash for random events (a CSS rgba string, '' = none): amber for a
   *  munitions overcharge, sickly green for gremlins, etc. Rendered as a HUD overlay. */
  eventTint: string;
  /** Hide the on-screen enemy reticles + off-screen arrow (The Long Dark eclipse: fight by radar
   *  and muzzle-flash alone). Radar keeps working. */
  hideMarks: boolean;
  /** A big cinematic announcement card for a random event (title + tagline + theme colour). Set on
   *  each event's reveal; the HUD sweeps it in, holds, and fades. null = nothing showing. */
  eventCard: { title: string; sub: string; color: string; at: number } | null;
  /** Touch FIRE button held — ORed with gamepad RT to drive the controlled unit's manual fire. */
  manualFiring: boolean;
  hitFlashAt: number;
  enemyArrow: EnemyArrow;
  radarBlips: RadarBlip[];
  radarFriendlies: RadarFriendly[];
  radarVillages: RadarVillage[];
  enemyMarks: EnemyMark[];
  /** Highest-priority current threat — drives the banner. null when things are calm. */
  alert: ThreatAlert | null;
  /** Bump each time radarBlips or enemyMarks are mutated in place so components re-render. */
  radarTick: number;
  /** Seconds timestamp when the player entered the death/crash sequence. */
  deathStartedAt: number;
  /** Seconds timestamp when the player's wreckage hit the ground (0 = not yet). */
  deathImpactAt: number;
  /** Why the run ended — 'shotdown' (player crashed) or 'villages' (all wiped out). */
  lossReason: 'shotdown' | 'villages';
  /** Live village count for HUD. */
  villagesAlive: number;
  villagesTotal: number;

  // --- Commander / possession ---
  /** Live allied units and their standing orders for the grouped command strip. */
  controlUnits: ControlUnitStatus[];
  /** Entity id of the currently-controlled unit. */
  controlledId: number;
  /** Aiming overlay for the controlled unit — 'crosshair' draws a gunsight (ground guns). */
  controlledReticle: 'none' | 'crosshair';
  /** Crewed-tank targeting computer overlay, or null. */
  tankTargeting: TankTargeting | null;
  /** Transient control-scheme hint shown right after taking over a unit. */
  controlHint: string;
  controlHintAt: number;
  /** Earned-reinforcement HUD readout: planes banked, and seconds until the next arrives
   *  (null when none is inbound — either nothing earned or the squadron is at full strength). */
  reinforceBanked: number;
  reinforceEtaSec: number | null;

  // --- Tactical map ---
  /** When true, the fullscreen map is up (the sim keeps running — it's a live command view). */
  mapOpen: boolean;
  /** Snapshot of the world the map renders, refreshed live while open. */
  mapData: MapData | null;
  /** Currently-selected friendly unit on the map (for issuing orders). Shared by pointer
   *  and gamepad so both input methods agree. */
  mapSelectedId: number | null;
  /** Gamepad cursor position on the map, in world X/Z. null = pointer-only (no reticle). */
  mapCursor: { x: number; z: number } | null;

  // --- Combo ---
  combo: number;
  comboExpiresAt: number; // seconds timestamp
  maxCombo: number;
  comboFlashAt: number;   // drives the "CLOSE!" / combo-up HUD pop
  comboFlashText: string;
  /** Monotonic enemy-kill counter — the Game loop watches it to fire the kill-confirm SFX. */
  kills: number;

  // --- Tutorial ---
  /** True while the interactive coached run is active — the wave director suppresses enemy spawns
   *  so it's a calm, no-fail sandbox, and the HUD shows the task checklist. */
  tutorial: boolean;
  /** Which coach tasks the player has completed this tutorial (keys in TUTORIAL_TASKS). */
  tutorialDone: Record<string, boolean>;

  // --- Meta-progression ---
  prog: Progression;
  /** Effective stats for the selected plane + perks, recomputed when loadout changes. */
  stats: EffectiveStats;
  creditsEarnedThisRun: number;

  setPhase: (p: Phase) => void;
  /** Begin/end the tutorial flag (set before resetRun so the run spawns in sandbox mode). */
  setTutorial: (on: boolean) => void;
  /** Mark a coach task complete (no-op if already done). */
  completeTask: (key: string) => void;
  setScore: (s: number) => void;
  addScore: (n: number) => void;
  /** Score a kill worth `basePoints` through the combo multiplier. */
  registerKill: (basePoints: number) => void;
  /** A near-miss of enemy fire — bumps combo without direct score. */
  registerNearMiss: () => void;
  /** Per-frame combo decay check (called from the game loop). */
  tickCombo: (now: number) => void;
  /** Spend credits to unlock a plane. Returns true on success. */
  buyPlane: (id: string) => boolean;
  /** Buy one level of a perk. Returns true on success. */
  buyPerk: (id: string) => boolean;
  /** Select an owned plane as the active one. */
  selectPlane: (id: string) => void;
  setWave: (w: number, introAt: number) => void;
  setHp: (hp: number, maxHp?: number) => void;
  takeDamage: () => void;
  setFiring: (v: boolean) => void;
  toggleMute: () => void;
  toggleSetting: (key: 'fxLite' | 'colorblind' | 'reducedMotion') => void;
  toggleAi: () => void;
  setBoost: (v: boolean) => void;
  setBoostThermal: (heat: number, overheat: boolean) => void;
  setFlightGauge: (rpmFrac: number, stallWarn: number) => void;
  setWhiteout: (v: number) => void;
  setEventTint: (v: string) => void;
  setHideMarks: (v: boolean) => void;
  showEventCard: (title: string, sub: string, color: string) => void;
  setManualFiring: (v: boolean) => void;
  setEnemyArrow: (a: EnemyArrow) => void;
  pushRadarTick: () => void;
  setAlert: (a: ThreatAlert | null) => void;
  setControlHint: (text: string) => void;
  setReinforce: (banked: number, etaSec: number | null) => void;
  setMapOpen: (open: boolean, data?: MapData | null) => void;
  setMapSelected: (id: number | null) => void;
  setMapCursor: (c: { x: number; z: number } | null) => void;
  beginDeath: (now: number) => void;
  markDeathImpact: (now: number) => void;
  setLossReason: (r: 'shotdown' | 'villages') => void;
  setVillageCounts: (alive: number, total: number) => void;
  setControlUnits: (units: ControlUnitStatus[], controlledId: number, reticle: 'none' | 'crosshair') => void;
  setTankTargeting: (t: TankTargeting | null) => void;
  resetRun: () => void;
}

const HISCORE_KEY = 'ww1-hiscore';

const readHi = (): number => {
  try {
    const v = localStorage.getItem(HISCORE_KEY);
    return v ? parseInt(v, 10) || 0 : 0;
  } catch {
    return 0;
  }
};

const writeHi = (n: number) => {
  try { localStorage.setItem(HISCORE_KEY, String(n)); } catch { /* ignore */ }
};

const initialProg = loadProgression();
const initialStats = effectiveStats(initialProg);

/** Combo → score multiplier. Every 5 combo adds +0.5×, capped at 5×. */
function comboMultiplier(combo: number): number {
  return Math.min(5, 1 + Math.floor(combo / 5) * 0.5);
}

export const useStore = create<GameState>((set, get) => ({
  phase: 'splash',
  tutorial: false,
  tutorialDone: {},
  score: 0,
  hiScore: readHi(),
  wave: 0,
  waveIntroAt: 0,
  hp: 20,
  maxHp: 20,
  isFiring: false,
  muted: false,
  ...loadSettings(),
  ai: false,
  boosting: false,
  boostHeat: 0,
  boostOverheat: false,
  rpmFrac: 1,
  stallWarn: 0,
  whiteout: 0,
  eventTint: '',
  hideMarks: false,
  eventCard: null,
  manualFiring: false,
  hitFlashAt: 0,
  enemyArrow: { any: false, offscreen: false, angle: 0, normDist: 1 },
  radarBlips: [],
  radarFriendlies: [],
  radarVillages: [],
  enemyMarks: [],
  alert: null,
  radarTick: 0,
  deathStartedAt: 0,
  deathImpactAt: 0,
  lossReason: 'shotdown',
  villagesAlive: 20,
  villagesTotal: 20,
  controlUnits: [],
  controlledId: 0,
  controlledReticle: 'none',
  tankTargeting: null,
  controlHint: '',
  controlHintAt: 0,
  reinforceBanked: 0,
  reinforceEtaSec: null,
  mapOpen: false,
  mapData: null,
  mapSelectedId: null,
  mapCursor: null,
  combo: 0,
  comboExpiresAt: 0,
  maxCombo: 0,
  kills: 0,
  comboFlashAt: 0,
  comboFlashText: '',
  prog: initialProg,
  stats: initialStats,
  creditsEarnedThisRun: 0,

  registerKill: (basePoints) => set((s) => {
    const now = performance.now() / 1000;
    // A kill within the window keeps the combo; otherwise it starts fresh at 1.
    const combo = (now < s.comboExpiresAt ? s.combo : 0) + 1;
    const mult = comboMultiplier(combo);
    return {
      score: s.score + Math.round(basePoints * mult),
      combo,
      comboExpiresAt: now + COMBO_WINDOW,
      maxCombo: Math.max(s.maxCombo, combo),
      comboFlashAt: now,
      comboFlashText: combo >= 2 ? `COMBO ×${combo}` : '',
      kills: s.kills + 1,
    };
  }),
  registerNearMiss: () => set((s) => {
    const now = performance.now() / 1000;
    if (now >= s.comboExpiresAt && s.combo === 0) {
      // No active combo — a near miss starts one and shows feedback.
      return { combo: 1, comboExpiresAt: now + COMBO_WINDOW, maxCombo: Math.max(s.maxCombo, 1), comboFlashAt: now, comboFlashText: 'CLOSE!' };
    }
    const combo = s.combo + 1;
    return {
      combo,
      comboExpiresAt: now + COMBO_WINDOW,
      maxCombo: Math.max(s.maxCombo, combo),
      comboFlashAt: now,
      comboFlashText: 'CLOSE!',
    };
  }),
  tickCombo: (now) => set((s) => (s.combo > 0 && now >= s.comboExpiresAt ? { combo: 0 } : {})),

  buyPlane: (id) => {
    const s = get();
    const def = PLANES.find((p) => p.id === id);
    if (!def || s.prog.ownedPlanes.includes(id) || s.prog.credits < def.cost) return false;
    const prog: Progression = {
      ...s.prog,
      credits: s.prog.credits - def.cost,
      ownedPlanes: [...s.prog.ownedPlanes, id],
    };
    saveProgression(prog);
    set({ prog, stats: effectiveStats(prog) });
    return true;
  },
  buyPerk: (id) => {
    const s = get();
    const def = PERKS.find((p) => p.id === id);
    if (!def) return false;
    const cur = s.prog.perkLevels[id] ?? 0;
    if (cur >= def.maxLevel) return false;
    const cost = def.costPerLevel * (cur + 1);
    if (s.prog.credits < cost) return false;
    const prog: Progression = {
      ...s.prog,
      credits: s.prog.credits - cost,
      perkLevels: { ...s.prog.perkLevels, [id]: cur + 1 },
    };
    saveProgression(prog);
    set({ prog, stats: effectiveStats(prog) });
    return true;
  },
  selectPlane: (id) => {
    const s = get();
    if (!s.prog.ownedPlanes.includes(id)) return;
    const prog: Progression = { ...s.prog, selectedPlane: id };
    saveProgression(prog);
    set({ prog, stats: effectiveStats(prog) });
  },

  setEnemyArrow: (enemyArrow) => set({ enemyArrow }),
  pushRadarTick: () => set((s) => ({ radarTick: s.radarTick + 1 })),
  setAlert: (alert) => set({ alert }),
  setControlHint: (controlHint) => set({ controlHint, controlHintAt: performance.now() / 1000 }),
  setReinforce: (reinforceBanked, reinforceEtaSec) => set({ reinforceBanked, reinforceEtaSec }),
  setMapOpen: (mapOpen, mapData) => set((s) => ({ mapOpen, mapData: mapData !== undefined ? mapData : s.mapData })),
  setMapSelected: (mapSelectedId) => set({ mapSelectedId }),
  setMapCursor: (mapCursor) => set({ mapCursor }),
  beginDeath: (deathStartedAt) => set({ deathStartedAt, deathImpactAt: 0 }),
  markDeathImpact: (deathImpactAt) => set({ deathImpactAt }),
  setLossReason: (lossReason) => set({ lossReason }),
  setVillageCounts: (villagesAlive, villagesTotal) => set({ villagesAlive, villagesTotal }),
  setControlUnits: (controlUnits, controlledId, controlledReticle) => set({ controlUnits, controlledId, controlledReticle }),
  setTankTargeting: (tankTargeting) => set({ tankTargeting }),
  setTutorial: (tutorial) => set({ tutorial, tutorialDone: {} }),
  completeTask: (key) => set((s) => (s.tutorialDone[key] ? {} : { tutorialDone: { ...s.tutorialDone, [key]: true } })),
  setPhase: (phase) => {
    if (phase === 'gameover' || phase === 'victory') {
      const s = get();
      if (s.score > s.hiScore) {
        writeHi(s.score);
        set({ hiScore: s.score });
      }
      // Award credits from this run's score and bank them; record the furthest wave reached.
      const earned = Math.round(s.score * CREDIT_RATE);
      const prog: Progression = {
        ...s.prog,
        credits: s.prog.credits + earned,
        bestWave: Math.max(s.prog.bestWave, s.wave),
      };
      saveProgression(prog);
      set({ creditsEarnedThisRun: earned, prog });
    }
    set({ phase });
  },
  setScore: (score) => set({ score }),
  addScore: (n) => set((s) => ({ score: s.score + n })),
  setWave: (wave, introAt) => set({ wave, waveIntroAt: introAt }),
  setHp: (hp, maxHp) => set((s) => ({ hp, maxHp: maxHp ?? s.maxHp })),
  takeDamage: () => set({ hitFlashAt: performance.now() / 1000 }),
  setFiring: (isFiring) => set({ isFiring }),
  toggleMute: () => set((s) => ({ muted: !s.muted })),
  toggleSetting: (key) => set((s) => {
    const next = { fxLite: s.fxLite, colorblind: s.colorblind, reducedMotion: s.reducedMotion, [key]: !s[key] };
    try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  }),
  toggleAi: () => set((s) => ({ ai: !s.ai })),
  setBoost: (boosting) => set({ boosting }),
  setBoostThermal: (boostHeat, boostOverheat) => set({ boostHeat, boostOverheat }),
  setFlightGauge: (rpmFrac, stallWarn) => set({ rpmFrac, stallWarn }),
  setWhiteout: (whiteout) => set({ whiteout }),
  setEventTint: (eventTint) => set({ eventTint }),
  setHideMarks: (hideMarks) => set({ hideMarks }),
  showEventCard: (title, sub, color) => set({ eventCard: { title, sub, color, at: performance.now() / 1000 } }),
  setManualFiring: (manualFiring) => set({ manualFiring }),
  resetRun: () => set((s) => ({
    phase: 'playing', score: 0, wave: 0,
    hp: s.stats.maxHp, maxHp: s.stats.maxHp,
    isFiring: false, boosting: false, boostHeat: 0, boostOverheat: false, rpmFrac: 1, stallWarn: 0, whiteout: 0, eventTint: '', hideMarks: false, eventCard: null, manualFiring: false, tankTargeting: null, deathStartedAt: 0, deathImpactAt: 0,
    combo: 0, comboExpiresAt: 0, maxCombo: 0, kills: 0, creditsEarnedThisRun: 0,
    mapOpen: false, mapData: null, mapSelectedId: null, mapCursor: null,
    controlHint: '', controlHintAt: 0, alert: null, reinforceBanked: 0, reinforceEtaSec: null,
    ai: false, // every run starts hand-flown; the AI toggle no longer persists across restarts
  })),
}));

// Re-export catalog helpers for UI components.
export { PLANES, PERKS, planeById };
